/**
 * useRideNotifications.js - Hook for real-time ride status notifications
 * Polls for ride status changes and triggers notifications + toasts
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import api from '../services/api';
import notificationService from '../services/notificationService';

// Status change messages for toasts
const STATUS_MESSAGES = {
  driver_assigned: {
    title: 'Driver Accepted! 🚗',
    getMessage: (ride) => `${ride.driver_name || 'Your driver'} is on the way`,
    type: 'success'
  },
  en_route_pickup: {
    title: 'Driver En Route 🚙',
    getMessage: (ride) => `${ride.driver_name || 'Driver'} is heading to pickup`,
    type: 'info'
  },
  arrived_pickup: {
    title: 'Driver Has Arrived! 📍',
    getMessage: (ride) => `${ride.driver_name || 'Your driver'} is waiting for you`,
    type: 'warning'
  },
  in_progress: {
    title: 'Ride Started 🚀',
    getMessage: (ride) => `On your way to ${ride.dropoff_location?.city || 'destination'}`,
    type: 'info'
  },
  completed: {
    title: 'Ride Completed! ✅',
    getMessage: (ride) => `Arrived at destination${ride.final_fare ? ` - $${ride.final_fare.toFixed(2)}` : ''}`,
    type: 'success'
  },
  cancelled: {
    title: 'Ride Cancelled ❌',
    getMessage: (ride) => `Ride ${ride.reference || ''} was cancelled`,
    type: 'error'
  }
};

const useRideNotifications = (userId, userRole, enabled = true) => {
  const [activeRides, setActiveRides] = useState([]);
  const previousStatusRef = useRef({}); // Track previous statuses to detect changes
  const isInitialLoadRef = useRef(true);

  // Show in-app toast notification
  const showToast = useCallback((status, ride) => {
    const config = STATUS_MESSAGES[status];
    if (!config) return;

    const toastOptions = {
      duration: status === 'arrived_pickup' ? 10000 : 5000, // Longer for arrival
      position: 'top-center'
    };

    switch (config.type) {
      case 'success':
        toast.success(config.getMessage(ride), { ...toastOptions, description: config.title });
        break;
      case 'error':
        toast.error(config.getMessage(ride), { ...toastOptions, description: config.title });
        break;
      case 'warning':
        toast.warning(config.getMessage(ride), { ...toastOptions, description: config.title });
        break;
      default:
        toast.info(config.getMessage(ride), { ...toastOptions, description: config.title });
    }
  }, []);

  // Show browser push notification
  const showPushNotification = useCallback((status, ride) => {
    switch (status) {
      case 'driver_assigned':
        notificationService.showDriverAcceptedNotification(ride);
        break;
      case 'en_route_pickup':
        notificationService.showDriverEnRouteNotification(ride);
        break;
      case 'arrived_pickup':
        notificationService.showDriverArrivedNotification(ride);
        break;
      case 'in_progress':
        notificationService.showRideStartedNotification(ride);
        break;
      case 'completed':
        notificationService.showRideCompletedNotification(ride);
        break;
      case 'cancelled':
        notificationService.showRideCancelledNotification(ride);
        break;
      default:
        break;
    }
  }, []);

  // Handle status change
  const handleStatusChange = useCallback((ride, newStatus, oldStatus) => {
    console.log(`Ride ${ride.reference} status changed: ${oldStatus} -> ${newStatus}`);
    
    // Show in-app toast
    showToast(newStatus, ride);
    
    // Show browser push notification (if permission granted)
    if (notificationService.getPermissionStatus() === 'granted') {
      showPushNotification(newStatus, ride);
    }
  }, [showToast, showPushNotification]);

  // Fetch rides and check for status changes
  const fetchRides = useCallback(async () => {
    if (!userId || !enabled) return;

    try {
      let endpoint = '';
      if (userRole === 'customer') {
        endpoint = '/rides/my-rides';
      } else if (userRole === 'worker') {
        // For workers/drivers, get their assigned rides
        endpoint = '/rides/my-rides'; // This will filter by driver_id on backend
      } else {
        return; // Owner doesn't need real-time ride notifications
      }

      const response = await api.get(endpoint);
      const rides = response.data || [];
      
      // Filter to active rides only
      const activeStatuses = ['requested', 'driver_assigned', 'en_route_pickup', 'arrived_pickup', 'in_progress'];
      const currentActive = rides.filter(r => activeStatuses.includes(r.status));
      
      // Check for status changes (skip on initial load)
      if (!isInitialLoadRef.current) {
        rides.forEach(ride => {
          const prevStatus = previousStatusRef.current[ride.id];
          if (prevStatus && prevStatus !== ride.status) {
            handleStatusChange(ride, ride.status, prevStatus);
          }
        });
      }
      
      // Update refs
      isInitialLoadRef.current = false;
      const newStatusMap = {};
      rides.forEach(ride => {
        newStatusMap[ride.id] = ride.status;
      });
      previousStatusRef.current = newStatusMap;
      
      setActiveRides(currentActive);
    } catch (error) {
      console.error('Failed to fetch rides for notifications:', error);
    }
  }, [userId, userRole, enabled, handleStatusChange]);

  // Set up polling
  useEffect(() => {
    if (!enabled || !userId) return;

    // Initial fetch
    fetchRides();

    // Poll every 10 seconds for status updates
    const pollInterval = setInterval(fetchRides, 10000);

    return () => clearInterval(pollInterval);
  }, [enabled, userId, fetchRides]);

  // Request notification permission on mount
  useEffect(() => {
    if (enabled && notificationService.getPermissionStatus() === 'default') {
      // We'll let the NotificationPrompt component handle this
      notificationService.registerServiceWorker();
    }
  }, [enabled]);

  return {
    activeRides,
    refreshRides: fetchRides
  };
};

export default useRideNotifications;
